<style lang="scss">
  @use "@sourcewhale/shared/svelte-design-system/assets/css/design-system/colors";
  @use "@sourcewhale/shared/svelte-design-system/assets/css/design-system/utils";
  @use "@sourcewhale/shared/svelte-design-system/assets/css/design-system/mixins";

  .snooze-input {
    display: flex;
    width: utils.in_rem(300px);
    flex-direction: column;
    align-items: center;
    border-radius: 20px;
    border: 1.5px solid #f7f7f7;
    background: white;
    box-shadow: 0px 4px 20px 0px #00000014;

    .snooze-input-heading {
      padding: utils.in_rem(12px) utils.in_rem(20px);
      width: 100%;

      > h3 {
        @include colors.color(black_90);
      }
    }

    .snooze-time-picker {
      width: 100%;
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      padding-top: utils.in_rem(8px);
      padding-right: utils.in_rem(20px);
      padding-bottom: utils.in_rem(32px);
      padding-left: utils.in_rem(20px);

      .snooze-time-picker-heading {
        padding: utils.in_rem(4px);

        > h4 {
          @include colors.color(black_90);
        }
      }

      .time-picker {
        width: 100%;
        @include colors.color(black_90);
        font-size: utils.in_rem(14px);
        font-weight: 400;
      }
    }

    .snooze-buttons {
      width: 100%;
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      padding: utils.in_rem(12px);
      @include colors.background-color(interfaceSwGrey_5);
      border-bottom-left-radius: 20px;
      border-bottom-right-radius: 20px;
      gap: utils.in_rem(12px);
    }
  }
</style>

<script>
  // Components
  import Button from "@sourcewhale/shared/svelte-design-system/components/atoms/Button.svelte";
  import ButtonWithTooltip from "@sourcewhale/shared/svelte-design-system/components/atoms/ButtonWithTooltip.svelte";
  import ListItem from "@sourcewhale/shared/svelte-design-system/components/molecules/Dropdown/ListItem.svelte";
  import FloatingUI from "@sourcewhale/shared/svelte-design-system/components/utils/FloatingUI.svelte";
  // Stores
  import { todoPanelStore, todosModalItemStore } from "../stores";
  // Utils
  import { dateForDateTimeInputValue, getTimezone } from "../../../../../assets/js/src/general/utils";
import { showToast } from "@sourcewhale/core";
  import { delayTodos } from "@sourcewhale/api";
  import {
    makeEscapeAction,
    makeWindowClick,
  } from "@sourcewhale/shared/src/svelte/utils/escapable";

  export let placement = "bottom-end";
  export let callBackLater = false;
  export let closeDropdown;

  // Default snooze time set reactively to handle user clicking 'Clear' inside the timePicker window
  let snoozeTime = null;
  $: if (!snoozeTime) {
    snoozeTime = dateForDateTimeInputValue(Date.now() / 1000 + 10 * 60);
  }

  let show = false;
  let el;
  let floatingEl;

  const submitSnooze = (opts) => {
    const { toDoIds, datetime, outcome, phoneCallNote, onSuccess } = opts;
    const body = {
      toDoIds,
      timezone: getTimezone(),
      datetime,
      outcome,
      phoneCallNote,
      kind: $todosModalItemStore.kind,
    };

    const current = dateForDateTimeInputValue(Date.now() / 1000);
    if (Date.parse(current) > Date.parse(body.datetime)) {
      showToast({
        title: `Please select a time in the future`,
        type: "danger",
      });
      return Promise.reject;
    }

    return delayTodos(body).then(() => {
      showToast({
        title: callBackLater ? `Rescheduled!` : `Snoozed!`,
        type: "success",
      });
      if (onSuccess) onSuccess();
    });
  };

  const handleShowSnoozeClick = () => {
    show = !show;
  };

  const closeSnoozeInput = () => {
    show = false;
  };

  const closeModal = () => {
    $todosModalItemStore = null;
  };

  const handleSubmitSnoozeBtnClick = async () => {
    const body = {
      toDoIds: [$todosModalItemStore.toDoId],
      datetime: snoozeTime,
      onSuccess: () => {
        closeSnoozeInput();

        // For the call back later option in the log call outcome dropdown
        if (closeDropdown) closeDropdown();

        // Go to next todo or close the modal
        if ($todosModalItemStore?.next) $todosModalItemStore.next();
        else closeModal();
      },
    };

    if (callBackLater) {
      body.outcome = "callBackLater";
      body.phoneCallNote = todoPanelStore.phoneCallNote;
    }

    await submitSnooze(body);
  };

  const showSnoozeBtnProps = {
    size: "small",
    kind: "tertiary-grey",
    leadingIcon: "fa-snooze fa-solid",
    onclick: handleShowSnoozeClick,
  };

  const cancelSnoozeBtnProps = {
    size: "large",
    kind: "ghost-blue",
    class: "flex-1",
    onclick: closeSnoozeInput,
  };

  const submitSnoozeBtnProps = {
    size: "large",
    kind: "primary",
    class: "flex-1",
    onclick: handleSubmitSnoozeBtnClick,
  };

  const callBackLaterBtnProps = {
    icon: "fa-phone-arrow-up-right",
    onclick: handleShowSnoozeClick,
  };

  const escape = makeEscapeAction(closeSnoozeInput);
  $: windowClick = makeWindowClick(closeSnoozeInput, el, floatingEl);
</script>

<svelte:window on:click={windowClick} />

{#if callBackLater}
  <div bind:this={el}>
    <ListItem {...callBackLaterBtnProps}>
      <span slot="tooltip">Reschedule this task until a later time</span>
      Call back later
    </ListItem>
  </div>
{:else}
  <ButtonWithTooltip bind:el placement="top" {...showSnoozeBtnProps}>
    <span slot="tooltip"
      >{$todosModalItemStore.kind === "opportunity"
        ? "Snooze opportunity to a later date"
        : "Postpone this task until a later date and time of your choice"}</span
    >
    Snooze
  </ButtonWithTooltip>
{/if}
{#if show}
  <FloatingUI
    referenceEl={el}
    bind:floatingEl
    class="z-index-1"
    {placement}
    {escape}
  >
    <div class="snooze-input">
      <div class="snooze-input-heading">
        <h3>{callBackLater ? `Call back later` : `Snooze task until`}</h3>
      </div>
      <div class="snooze-time-picker">
        <div class="snooze-time-picker-heading">
          <h4>Select date and time</h4>
        </div>
        <!-- TODO: Replace timePicker with a custom built component -->
        <input
          class="time-picker"
          type="datetime-local"
          bind:value={snoozeTime}
        />
      </div>
      <div class="snooze-buttons">
        <Button {...cancelSnoozeBtnProps}>Cancel</Button>
        <Button {...submitSnoozeBtnProps}>
          {callBackLater ? `Apply` : `Snooze`}
        </Button>
      </div>
    </div>
  </FloatingUI>
{/if}
